import React, { Suspense, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { Menu } from "primereact/menu";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";

import PathConstants from "routes/pathConstants";
import { useAuth } from "context/AuthContext";

import ChangePassword from "components/layout/ChangePassword";
import { itemRenderer } from "components/layout/navItem.template";
import TopBar from "components/layout/TopBar";

const LoggedInLayout = () => {
  const { removeToken, userDetails, loginType } = useAuth();

  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);

  const location = useLocation();

  const toggleChangePassword = () => {
    setChangePasswordVisible(!changePasswordVisible);
  };

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const loggedInItems = [
    {
      label: "Change Password",
      icon: "pi pi-lock",
      command: () => toggleChangePassword(),
    },
    {
      label: "Logout",
      icon: "pi pi-sign-out",
      command: () => removeToken(),
    },
  ];

  const navItemsOperators = [
    {
      label: "Home",
      icon: "pi pi-home",
      url: PathConstants.OPERATORS_SEARCH,
      template: (item, options) => itemRenderer(item, options, location),
    },
    {
      label: "Objections",
      icon: "pi  pi-exclamation-circle",
      url: PathConstants.DASHBOARD_OBJECTIONS,
      template: (item, options) => itemRenderer(item, options, location),
    },
    {
      label: "Transactions",
      icon: "pi pi-receipt",
      className: "z-4",
      template: (item, options) => itemRenderer(item, options, location),
      items: [
        {
          label: "Offline Transactions",
          icon: "pi pi-wallet",
          url: PathConstants.TRANSACTIONS_OFFLINE,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Archived Transactions",
          icon: "pi pi-trash",
          url: PathConstants.ARCHIVED_TRANSACTIONS,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Posting Transactions",
          icon: "pi pi-plus-circle",
          url: PathConstants.TRANSACTIONS_POSTING_NIGAM,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Ledger",
          icon: "pi pi-book",
          url: PathConstants.LEDGER,
          template: (item, options) => itemRenderer(item, options, location),
        },
      ],
    },
  ];

  const navItemsAdmin = [
    {
      id: "masters",
      label: "Masters",
      items: [
        {
          label: "Officials",
          icon: "pi pi-users",
          url: PathConstants.CONFIG_OFFICIALS,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Operators",
          icon: "pi pi-desktop",
          url: PathConstants.CONFIG_OPERATORS,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Zones",
          icon: "pi pi-sitemap",
          url: PathConstants.CONFIG_ZONES,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Wards",
          icon: "pi pi-map",
          url: PathConstants.CONFIG_WARDS,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Mohallas",
          icon: "pi pi-map-marker",
          url: PathConstants.CONFIG_MOHALLAS,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Discounts",
          icon: "pi pi-percentage",
          url: PathConstants.CONFIG_DISCOUNTS,
          template: (item, options) => itemRenderer(item, options, location),
        },
      ],
    },
  ];

  const navItemsSuperAdmin = [
    {
      id: "gis-reports",
      label: "GIS Reports",
      items: [
        {
          label: "Report Dashboard",
          icon: "pi pi-file",
          url: PathConstants.REPORT_DASHBOARD,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "M3-Report",
          icon: "pi pi-ellipsis-h",
          url: PathConstants.REPORT_GIS,
          template: (item, options) => itemRenderer(item, options, location),
        },
      ],
    },
    {
      id: "logs",
      label: "Logs",
      items: [
        {
          label: "Logs",
          icon: "pi pi-align-left",
          url: PathConstants.LOGS,
          template: (item, options) => itemRenderer(item, options, location),
        },
      ],
    },
    {
      id: "utilities",
      label: "Utilities",
      items: [
        {
          label: "Utilities",
          icon: "pi pi-sliders-h",
          url: PathConstants.ADMIN_UTILITY,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Roles",
          icon: "pi pi-users",
          url: PathConstants.CONFIG_ROLES,
          template: (item, options) => itemRenderer(item, options, location),
        },
      ],
    },
  ];

  const navItemsNigam = [
    {
      label: "Properties",
      template: (item, options) => itemRenderer(item, options, location),
      items: [
        {
          label: "Properties",
          icon: "pi pi-building",
          url: PathConstants.PROPERTIES,
          template: (item, options) => itemRenderer(item, options, location),
          active: true,
        },
        {
          label: "Properties-GIS",
          icon: "pi pi-map",
          url: PathConstants.GIS_MODULE,
          template: (item, options) => itemRenderer(item, options, location),
          active: true,
        },
        {
          label: "Edited Properties",
          icon: "pi pi-pen-to-square",
          url: PathConstants.EDITED_PROPERTIES,
          visible: ["ctao", "admin", "nigam_admin"].includes(
            userDetails?.accessName
          ),
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Split Properties",
          icon: "pi pi-arrow-up-right-and-arrow-down-left-from-center",
          url: PathConstants.PROPERTY_SPLIT,
          visible: ["ctao", "admin", "nigam_admin"].includes(
            userDetails?.accessName
          ),
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Add Property",
          icon: "pi pi-plus-circle",
          url: PathConstants.ADD_PROPERTY,
          visible: userDetails?.accessName === "admin",
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Old Properties",
          icon: "pi pi-history",
          url: PathConstants.OLD_PROPERTIES,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Notices",
          icon: "pi pi-file",
          url: PathConstants.PROPERTY_NOTICES,
          template: (item, options) => itemRenderer(item, options, location),
        },
      ],
    },

    {
      label: "Objections",
      items: [
        {
          label: "Objections",
          icon: "pi  pi-exclamation-circle",
          url: PathConstants.DASHBOARD_OBJECTIONS,
          template: (item, options) => itemRenderer(item, options, location),
        },
      ],
    },

    {
      label: "Mutations",
      template: (item, options) => itemRenderer(item, options, location),
      items: [
        {
          label: "Mutations",
          icon: "pi pi-user-edit",
          url: PathConstants.MUTATIONS,
          template: (item, options) => itemRenderer(item, options, location),
          active: true,
        },
      ],
    },

    {
      label: "Reports",
      template: (item, options) => itemRenderer(item, options, location),
      items: [
        {
          label: "Demand Reports",
          icon: "pi  pi-chart-pie",
          url: PathConstants.DEMAND_REPORTS,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Defaulters Reports",
          icon: "pi pi-thumbs-down",
          url: PathConstants.DEFAULTERS_LIST,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "ARV Reports",
          icon: "pi pi-warehouse",
          url: PathConstants.ARV_LIST,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Collection Reports",
          icon: "pi pi-indian-rupee",
          url: PathConstants.COLLECTION_REPORT,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Collection List",
          icon: "pi pi-list-check",
          url: PathConstants.COLLECTION_LIST_REPORT,
          template: (item, options) => itemRenderer(item, options, location),
        },
      ],
    },
    {
      label: "Transactions",
      template: (item, options) => itemRenderer(item, options, location),
      items: [
        {
          label: "Online Transactions",
          icon: "pi pi-credit-card",
          url: PathConstants.ALL_ONLINE_TRANSACTIONS,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Offline Transactions",
          icon: "pi pi-wallet",
          url: PathConstants.TRANSACTIONS_OFFLINE,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "NEFT Transactions",
          icon: "pi pi-money-bill",
          url: PathConstants.TRANSACTIONS_NEFT,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Posting Transactions",
          icon: "pi pi-plus-circle",
          url: PathConstants.TRANSACTIONS_POSTING_NIGAM,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Archived Transactions",
          icon: "pi pi-trash",
          url: PathConstants.ARCHIVED_TRANSACTIONS,
          template: (item, options) => itemRenderer(item, options, location),
        },
        {
          label: "Ledger",
          icon: "pi pi-book",
          url: PathConstants.LEDGER,
          template: (item, options) => itemRenderer(item, options, location),
        },
      ],
    },

    ...(["admin", "ctao"].includes(userDetails?.accessName)
      ? navItemsAdmin
      : []),

    ...(userDetails?.isSuperAdmin ? navItemsSuperAdmin : []),
  ];

  const navItemsJalkal = [
    {
      label: "Home",
      icon: "pi pi-home",
      url: PathConstants.JALKAL_DASHBOARD,
      template: (item, options) => itemRenderer(item, options, location),
    },
  ];

  const getNavItems = (loginType) => {
    if (loginType === "nagar_nigam") {
      return navItemsNigam;
    } else {
      return navItemsJalkal;
    }
  };

  return (
    <>
      <TopBar
        toggleSidebar={toggleSidebar}
        loggedInItems={loggedInItems}
        userDetails={userDetails}
      />

      <div className="flex h-screen" style={{ paddingTop: "3.5rem" }}>
        {showSidebar && (
          <div>
            <Menu
              model={loginType ? getNavItems(loginType) : navItemsOperators}
              className="shadow-1 border-1 border-primary-100 border-noround w-15rem md:w-15rem overflow-auto h-full"
              closeOnEscape={false}
            />
          </div>
        )}

        <div className="w-full overflow-auto p-2">
          <Suspense
            fallback={() => (
              <div className="flex align-items-center justify-content-center">
                <ProgressSpinner />
              </div>
            )}
          >
            {userDetails ? (
              <Outlet />
            ) : (
              <div className="flex align-items-center justify-content-center">
                <ProgressSpinner />
              </div>
            )}
          </Suspense>
        </div>
      </div>

      <Dialog
        onHide={toggleChangePassword}
        header="Change Password"
        style={{ minWidth: "40vw" }}
        visible={changePasswordVisible}
      >
        <ChangePassword />
      </Dialog>
    </>
  );
};

export default LoggedInLayout;
